/**
 * ServiceTypeSelect — seletor ÚNICO de tipo de serviço (single ou multiple).
 *
 * Mostra apenas as 3 últimas hierarquias do catálogo — `grupo › subgrupo › item`
 * — porque a categoria (Residencial/Automóvel/Técnico) já é contexto da tela.
 * Só a ÚLTIMA hierarquia é clicável: o item quando o subgrupo tem itens (4º
 * nível), senão o próprio subgrupo (3º nível). Grupo e subgrupo com filhos são
 * apenas rótulos de agrupamento.
 *
 * Reuso em qualquer tela:
 *   • `categoria` (slug) → carrega a árvore de /service-catalog/tree e filtra.
 *   • `options` → lista pronta de folhas; use `tiposComoOpcoes()` para converter
 *     os tipos de serviço já cadastrados no tenant (/service-types).
 *   • single:   `value` = id da folha, `onChange(folha | null)`.
 *   • multiple: `value` = array de ids, `onChange(ids, folhas)`.
 *   • `clearable` (padrão) mostra o "x" no gatilho para desmarcar.
 */
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Loader2, X } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import api from "../utils/api";
import { renderLucideIcon } from "./common/IconPicker";

const norm = (s) =>
  (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Trilha das 3 últimas hierarquias, sem buracos. */
const trilhaDe = (o) => {
  // Caminho real da hierarquia; o apelido da folha substitui a última parte.
  const partes = [o.grupo_nome, o.subgrupo_nome, o.item_nome]
    .map((p) => (p || "").trim())
    .filter(Boolean);
  const folha = (o.label || "").trim();
  if (!partes.length) return folha ? [folha] : [];
  if (folha) partes[partes.length - 1] = folha;
  return partes;
};

/** Aceita o id da folha ou o id do nível dela (grupo folha, subgrupo ou item). */
const casaComValor = (folha, valor) =>
  !!valor &&
  (folha.id === valor ||
    folha.item_id === valor ||
    (!folha.item_id && folha.subgrupo_id === valor) ||
    (!folha.item_id && !folha.subgrupo_id && folha.grupo_id === valor));

/** Caminho legível: `Chaveiro Automóvel › Abertura › Nacional`. */
export const caminhoServico = (o) => (o ? trilhaDe(o).join(" › ") : "");

/** Converte tipos de serviço do tenant (/service-types) em folhas do seletor. */
export const tiposComoOpcoes = (tipos = []) =>
  (tipos || []).map((t) => ({
    ...t,
    id: t.id,
    grupo_id: t.catalog_grupo_id || null,
    subgrupo_id: t.catalog_subgrupo_id || null,
    item_id: t.catalog_item_id || null,
    grupo_nome: t.grupo_nome || "",
    subgrupo_nome: t.subgrupo_nome || "",
    item_nome: t.item_nome || "",
    label: t.servico_nome || t.nome || "",
    nome_abreviado: t.nome_abreviado || "",
    tarifas: t.tarifas || [],
  }));

/** Achata a árvore do catálogo em folhas selecionáveis. */
export function folhasDoCatalogo(categorias, categoriaSlug) {
  const folhas = [];
  (categorias || [])
    .filter((c) => !categoriaSlug || (c.slug || c.id) === categoriaSlug)
    .forEach((cat) =>
      (cat.grupos || []).forEach((g) => {
        // Grupo sem subgrupos é a própria folha (hierarquia de 2 níveis).
        if (!(g.subgrupos || []).length) {
          folhas.push({
            categoria_slug: cat.slug || cat.id,
            categoria_nome: cat.nome,
            grupo_id: g.id,
            grupo_nome: g.nome,
            subgrupo_id: null,
            subgrupo_nome: "",
            id: g.id,
            item_id: null,
            item_nome: "",
            label: (g.nome_servico || "").trim() || g.nome,
            nome_abreviado: g.nome_abreviado || "",
            tarifas: g.tarifas || [],
          });
          return;
        }
        (g.subgrupos || []).forEach((s) => {
          const itens = s.itens || [];
          const base = {
            categoria_slug: cat.slug || cat.id,
            categoria_nome: cat.nome,
            grupo_id: g.id,
            grupo_nome: g.nome,
            subgrupo_id: s.id,
            subgrupo_nome: s.nome,
          };
          if (itens.length) {
            itens.forEach((it) =>
              folhas.push({
                ...base,
                id: it.id,
                item_id: it.id,
                item_nome: it.nome,
                label: (it.nome_servico || "").trim() || it.nome,
                nome_abreviado: it.nome_abreviado || "",
                tarifas: it.tarifas || [],
              }),
            );
          } else {
            folhas.push({
              ...base,
              id: s.id,
              item_id: null,
              item_nome: "",
              label: (s.nome_servico || "").trim() || s.nome,
              nome_abreviado: s.nome_abreviado || "",
              tarifas: s.tarifas || [],
            });
          }
        });
      }),
    );
  return folhas;
}

// Layout ÚNICO: mesma leitura do popover "Mover" do Catálogo de Serviços —
// categoria › grupo › subgrupo › serviço, com indentação, ícone de cada nó e os
// níveis que não são folha apenas como contexto (desabilitados).
const PAD_ARVORE = { categoria: "pl-2", grupo: "pl-6", subgrupo: "pl-10", item: "pl-14" };
const PESO_ARVORE = {
  categoria: "font-semibold text-gray-800",
  grupo: "font-medium text-gray-700",
  subgrupo: "text-gray-700",
  item: "text-gray-600",
};
const ROTULO_ARVORE = { categoria: "categoria", grupo: "grupo", subgrupo: "subgrupo" };

export default function ServiceTypeSelect({
  value = null,
  onChange,
  multiple = false,
  categoria = null,
  options = null,
  placeholder = "Selecione no catálogo...",
  allLabel = "Todos os tipos",
  emptyText = "Nenhum serviço encontrado.",
  clearable = true,
  disabled = false,
  align = "start",
  popoverModal = false,
  testid = "service-type-select",
  triggerClassName = "h-10",
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const [arvore, setArvore] = useState(null);

  // A árvore do catálogo é sempre a fonte da EXIBIÇÃO (mesmo layout em todas as
  // telas). Quando a tela passa `options`, elas definem o que é selecionável.
  useEffect(() => {
    if (arvore) return;
    api
      .get("/service-catalog/tree")
      .then(({ data }) => setArvore(data))
      .catch(() => setArvore([]));
  }, [arvore]);

  const folhas = useMemo(() => {
    if (options) return options.map((o) => ({ ...o, id: o.id ?? o.item_id ?? o.subgrupo_id }));
    return folhasDoCatalogo(arvore, categoria);
  }, [options, arvore, categoria]);

  const arvoreFiltrada = useMemo(
    () => (arvore || []).filter((c) => !categoria || (c.slug || c.id) === categoria),
    [arvore, categoria],
  );

  // Folha indexada pelo id do NÓ do catálogo: a árvore mostra os nós, e o que
  // a tela pode escolher pode ser o tipo de serviço do tenant (id diferente).
  const folhaPorId = useMemo(() => {
    const mapa = new Map();
    folhas.forEach((f) => {
      mapa.set(f.item_id || f.subgrupo_id || f.grupo_id || f.id, f);
      if (f.id) mapa.set(f.id, f);
    });
    return mapa;
  }, [folhas]);

  // Poda os ramos sem nada selecionável (ex.: catálogo inteiro x os poucos
  // tipos cadastrados no tenant).
  const arvoreUtil = useMemo(() => {
    const tem = (id) => folhaPorId.has(id);
    return arvoreFiltrada
      .map((cat) => {
        const grupos = (cat.grupos || [])
          .map((g) => {
            const subs = (g.subgrupos || [])
              .map((s) => {
                const itens = (s.itens || []).filter((it) => tem(it.id));
                if (itens.length) return { ...s, itens };
                return tem(s.id) ? { ...s, itens: [] } : null;
              })
              .filter(Boolean);
            if (subs.length) return { ...g, subgrupos: subs };
            return tem(g.id) ? { ...g, subgrupos: [] } : null;
          })
          .filter(Boolean);
        return grupos.length ? { ...cat, grupos } : null;
      })
      .filter(Boolean);
  }, [arvoreFiltrada, folhaPorId]);

  const ids = useMemo(
    () => (multiple ? (Array.isArray(value) ? value : []) : []),
    [multiple, value],
  );
  const marcadas = useMemo(() => new Set(ids), [ids]);

  const selecionada = useMemo(
    () => (multiple ? null : folhas.find((f) => casaComValor(f, value)) || null),
    [multiple, folhas, value],
  );

  const escolher = (folha) => {
    if (multiple) {
      const proximos = marcadas.has(folha.id)
        ? ids.filter((x) => x !== folha.id)
        : [...ids, folha.id];
      onChange?.(
        proximos,
        folhas.filter((f) => proximos.includes(f.id)),
      );
      return;
    }
    onChange?.(folha);
    setBusca("");
    setAberto(false);
  };

  const limpar = () => (multiple ? onChange?.([], []) : onChange?.(null));

  const linhasArvore = () => {
    const out = [];
    const add = (nivel, no, iconeHerdado, caminho) => {
      const icone = no.icon_name || iconeHerdado;
      const folha = folhaPorId.get(no.id);
      const marcada = folha
        ? multiple
          ? marcadas.has(folha.id)
          : casaComValor(folha, value)
        : false;
      out.push(
        <CommandItem
          key={`${nivel}-${no.id}`}
          value={`${caminho} ${no.nome} ${no.nome_abreviado || ""}`}
          disabled={!folha}
          className={`${PAD_ARVORE[nivel]} pr-2 ${folha ? "" : "opacity-45"}`}
          onSelect={() => folha && escolher(folha)}
          data-testid={folha ? `${testid}-option-${no.id}` : `${testid}-node-${no.id}`}
        >
          {nivel !== "categoria" && <span className="text-gray-300 mr-1.5 shrink-0">└</span>}
          {folha && multiple ? (
            <span
              className={`mr-2 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                marcada
                  ? "border-transparent bg-[var(--accent)] text-white"
                  : "border-gray-300 bg-white"
              }`}
            >
              {marcada && <Check size={12} />}
            </span>
          ) : null}
          <span className="mr-1.5 text-gray-400 shrink-0">
            {renderLucideIcon(icone || "Wrench", { size: 13 })}
          </span>
          <span className={`flex-1 truncate text-sm ${PESO_ARVORE[nivel]}`}>{no.nome}</span>
          {folha && marcada && !multiple && (
            <Check size={14} className="ml-2 shrink-0 text-gray-700" />
          )}
          {!folha && (
            <span className="text-[10px] uppercase tracking-wider text-gray-400 ml-2 shrink-0">
              {ROTULO_ARVORE[nivel]}
            </span>
          )}
        </CommandItem>,
      );
    };
    arvoreUtil.forEach((cat) => {
      add("categoria", cat, cat.icon_name, "");
      (cat.grupos || []).forEach((g) => {
        add("grupo", g, g.icon_name || cat.icon_name, cat.nome);
        (g.subgrupos || []).forEach((sub) => {
          add("subgrupo", sub, sub.icon_name || g.icon_name || cat.icon_name, `${cat.nome} ${g.nome}`);
          (sub.itens || []).forEach((it) =>
            add(
              "item",
              it,
              it.icon_name || sub.icon_name || g.icon_name || cat.icon_name,
              `${cat.nome} ${g.nome} ${sub.nome}`,
            ),
          );
        });
      });
    });
    return out;
  };

  const carregando = arvore === null;
  const rotulo = multiple
    ? ids.length === 0
      ? ""
      : ids.length === 1
        ? caminhoServico(folhas.find((f) => f.id === ids[0])) || `${ids.length} selecionado`
        : `${ids.length} selecionados`
    : caminhoServico(selecionada);
  const temSelecao = multiple ? ids.length > 0 : !!selecionada;

  return (
    <Popover
      open={aberto}
      modal={popoverModal}
      onOpenChange={(v) => {
        if (disabled) return;
        setAberto(v);
        if (!v) setBusca("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={aberto}
          disabled={disabled}
          title={rotulo || undefined}
          className={`group relative w-full ${triggerClassName} bg-white justify-between gap-2 text-sm rounded-lg font-normal`}
          data-testid={`${testid}-trigger`}
        >
          <span className={`truncate ${temSelecao ? "text-gray-900" : "text-gray-400"}`}>
            {rotulo || (multiple ? allLabel : placeholder)}
          </span>
          {/* Tooltip CSS-only com o caminho completo (não altera o cursor) */}
          {!!rotulo && (
            <span className="pointer-events-none absolute left-0 bottom-full mb-1.5 hidden group-hover:block max-w-[420px] rounded-md bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium leading-snug text-white shadow-lg z-[1800] whitespace-normal text-left">
              {rotulo}
            </span>
          )}
          {carregando ? (
            <Loader2 size={14} className="text-gray-400 shrink-0 animate-spin" />
          ) : clearable && temSelecao && !disabled ? (
            // O `Button` do shadcn aplica `[&_svg]:pointer-events-none`; o span
            // reativa o ponteiro para o clique do "x" não abrir a lista.
            <span
              role="button"
              tabIndex={-1}
              aria-label="Limpar seleção"
              className="pointer-events-auto shrink-0 text-gray-400 hover:text-gray-700"
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); limpar(); }}
              data-testid={`${testid}-clear`}
            >
              <X size={14} />
            </span>
          ) : (
            <ChevronDown size={15} className="text-gray-400 shrink-0" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[420px] p-0 !z-[1750]"
        align={align}
        sideOffset={4}
      >
        <Command filter={(val, search) => (norm(val).includes(norm(search)) ? 1 : 0)}>
          <CommandInput
            placeholder="Buscar serviço..."
            className="h-9"
            value={busca}
            onValueChange={setBusca}
            data-testid={`${testid}-search`}
          />
          <CommandList className="max-h-[320px]" onWheel={(e) => e.stopPropagation()}>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>{linhasArvore()}</CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
